import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import styled from '@emotion/styled';

import { FontWeight } from '@clutter/clean';

import {
  SelectedItem,
  SelectedItems,
} from '@root/components/checkout/product_pages/subcomponents/plan_estimator_modal/types';
import type { Plan } from '@root/constants/pricing';

import { suggestedPlanForCUFT } from '@utils/plan';

import closeIcon from '@images/checkout/challenger/close.svg';
import minusIcon from '@images/checkout/challenger/minus.svg';
import plusIcon from '@images/checkout/challenger/plus.svg';

import { useMobileKeyboardInset } from './hooks/use_mobile_keyboard_inset';
import { useModalFocus } from './hooks/use_modal_focus';
import { TABLET_UP as DESKTOP, MOBILE } from './media';
import { StorageCalculatorSearch } from './storage_calculator_search';
import { CHALLENGER as T } from './tokens';

/**
 * Challenger storage calculator chrome (Figma).
 * Search/add behavior matches classic PlanEstimatorModal — popular
 * categories only after focusing search (not on open).
 */

/** List height — desktop 707:8059 (400). Mobile list flexes with the keyboard. */
const CONTENT_SLOT_HEIGHT = 400;

/** Figma mobile Modal 707:16293 — 370×617. */
const DIALOG_HEIGHT_MOBILE = 617;

const Overlay = styled.div<{ $vvHeight?: number }>`
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  justify-content: safe center;
  align-items: center;
  padding: ${T.space[16]};
  box-sizing: border-box;
  background: rgba(4, 7, 17, 0.4);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  ${({ $vvHeight }) =>
    $vvHeight
      ? `--storage-calc-vv-height: ${$vvHeight}px;`
      : `--storage-calc-vv-height: 100dvh;`}

  ${MOBILE} {
    overflow: hidden;
    overscroll-behavior: none;
    /* Top-align so the dialog does not jump when the keyboard opens/closes. */
    justify-content: flex-start;
  }
`;

const Dialog = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${T.space[24]};
  width: 100%;
  max-width: 560px;
  box-sizing: border-box;
  border-radius: 16px;
  background: ${T.color.bgPage};
  font-family: ${T.font.sans};
  color: ${T.color.textPrimary};
  overflow: hidden;

  ${MOBILE} {
    flex: 0 1 ${DIALOG_HEIGHT_MOBILE}px;
    height: ${DIALOG_HEIGHT_MOBILE}px;
    max-height: min(
      ${DIALOG_HEIGHT_MOBILE}px,
      calc(var(--storage-calc-vv-height, 100dvh) - 32px)
    );
    min-height: 0;
    padding: ${T.space[24]} 0 ${T.space[16]};
  }

  ${DESKTOP} {
    max-height: min(810px, calc(100vh - 32px));
    max-height: min(810px, calc(100dvh - 32px));
    padding: ${T.space[24]} ${T.space[24]} ${T.space[16]};
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  flex-shrink: 0;
  box-sizing: border-box;

  ${MOBILE} {
    gap: ${T.space[12]};
    padding: 0 ${T.space[24]};
  }

  ${DESKTOP} {
    gap: ${T.space[16]};
  }
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
  gap: ${T.space[16]};
`;

const Title = styled.h2`
  margin: 0;
  flex: 1;
  font-weight: ${FontWeight.Medium};
  color: ${T.color.textPrimary};

  ${MOBILE} {
    font-size: 16px;
    line-height: 22px;
  }

  ${DESKTOP} {
    font-size: 20px;
    line-height: 23px;
  }
`;

const CloseButton = styled.button`
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  flex-shrink: 0;

  img {
    display: block;
    width: 14px;
    height: 14px;
  }
`;

const Subtitle = styled.p`
  margin: 0;
  font-weight: ${FontWeight.Regular};
  color: ${T.color.textPrimary};

  ${MOBILE} {
    font-size: 13px;
    line-height: 18px;
  }

  ${DESKTOP} {
    font-size: 14px;
    line-height: 20px;
  }
`;

const SearchSection = styled.div`
  position: relative;
  z-index: 6;
  width: 100%;
  flex-shrink: 0;
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${T.space[16]};
  width: 100%;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  box-sizing: border-box;

  ${MOBILE} {
    padding: 0 ${T.space[16]};
  }
`;

const ContentSlot = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 0;
  width: 100%;
  flex: 1;
  min-width: 0;
  min-height: 0;
  overflow: hidden;

  ${DESKTOP} {
    min-height: ${CONTENT_SLOT_HEIGHT}px;
  }
`;

const ItemList = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 0 ${T.space[8]};
  box-sizing: border-box;
  border: 1px solid ${T.color.borderDefault};
  border-radius: ${T.radius.md};
  background: ${T.color.bgPrimary};

  scrollbar-width: thin;
  scrollbar-color: rgba(154, 161, 165, 0.5) transparent;

  flex: 1;
  min-height: 0;
  overscroll-behavior: contain;

  ${DESKTOP} {
    max-height: ${CONTENT_SLOT_HEIGHT}px;
  }
`;

const ItemRow = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  width: 100%;
  padding-top: ${T.space[12]};
  padding-bottom: ${T.space[12]};
  box-sizing: border-box;

  ${MOBILE} {
    gap: ${T.space[12]};
    padding-left: ${T.space[8]};
    padding-right: ${T.space[8]};
  }

  ${DESKTOP} {
    gap: ${T.space[24]};
    padding-left: ${T.space[16]};
    padding-right: ${T.space[16]};
  }

  & + & {
    border-top: 1px solid ${T.color.borderDefault};
  }
`;

const ItemName = styled.div`
  flex: 1 1 0;
  max-width: 280px;
  min-width: 0;
  font-weight: ${FontWeight.Regular};
  color: ${T.color.textPrimary};

  ${MOBILE} {
    font-size: 13px;
    line-height: 18px;
  }

  ${DESKTOP} {
    font-size: 14px;
    line-height: 20px;
  }
`;

const ItemActions = styled.div`
  display: flex;
  flex: 1 1 0;
  align-items: center;
  justify-content: flex-end;
  min-width: 0;

  ${MOBILE} {
    gap: ${T.space[12]};
  }

  ${DESKTOP} {
    gap: ${T.space[24]};
  }
`;

const RemoveButton = styled.button`
  appearance: none;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  font-family: ${T.font.sans};
  font-weight: ${FontWeight.Semibold};
  color: ${T.color.textTertiary};
  white-space: nowrap;
  text-align: right;

  ${MOBILE} {
    font-size: 12px;
    line-height: 14px;
  }

  ${DESKTOP} {
    font-size: 14px;
    line-height: 18px;
  }

  &:hover {
    color: ${T.color.textPrimary};
  }
`;

const Counter = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  flex-shrink: 0;
  padding: 5px 4px;
  border-radius: ${T.radius.sm};
  background: ${T.color.bgPage};
  box-sizing: border-box;
`;

const CounterButton = styled.button`
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  border-radius: ${T.radius.pill};
  background: ${T.color.bgButtonDisabled};
  cursor: pointer;

  img {
    display: block;
    width: 15px;
    height: 15px;
    flex-shrink: 0;
  }

  &:hover {
    opacity: 0.85;
  }
`;

const CounterValue = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  padding: 0 2px;
  box-sizing: border-box;
  font-weight: ${FontWeight.Medium};
  color: ${T.color.textPrimary};
  text-align: center;

  ${MOBILE} {
    font-size: 13px;
    line-height: 18px;
  }

  ${DESKTOP} {
    font-size: 14px;
    line-height: 21px;
  }
`;

const Footer = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  width: 100%;
  flex-shrink: 0;
  box-sizing: border-box;
  position: relative;
  z-index: 5;
  background: ${T.color.bgPage};

  ${MOBILE} {
    padding: 0 ${T.space[24]};
    margin-top: auto;
  }
`;

const EstimateButton = styled.button<{ disabled?: boolean }>`
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 48px;
  padding: 0 ${T.space[24]};
  border: none;
  border-radius: ${T.radius.sm};
  background: ${({ disabled }) =>
    disabled ? T.color.bgButtonDisabled : T.color.textBrand};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  font-family: ${T.font.sans};
  font-weight: ${FontWeight.Semibold};
  color: ${({ disabled }) =>
    disabled ? T.color.textDisabled : T.color.textInverse};
  white-space: nowrap;

  ${MOBILE} {
    font-size: 14px;
    line-height: 12px;
  }

  ${DESKTOP} {
    font-size: 16px;
    line-height: 16px;
  }

  &:hover {
    opacity: ${({ disabled }) => (disabled ? 1 : 0.9)};
  }
`;

type Props = {
  isOpen: boolean;
  selectedItems: SelectedItems;
  onSelectedItemsChange: (items: SelectedItems) => void;
  onClose: (plan?: Plan) => void;
};

export const StorageCalculatorModal: React.FC<Props> = ({
  isOpen,
  selectedItems,
  onSelectedItemsChange,
  onClose,
}) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const itemListRef = useRef<HTMLDivElement>(null);
  const itemListScrollTopRef = useRef(0);
  const keyboardOpenRef = useRef(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  useModalFocus(isOpen, dialogRef, searchInputRef);
  const keyboard = useMobileKeyboardInset(isOpen);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const [query, setQuery] = useState('');

  const persistItemListScroll = useCallback(() => {
    const list = itemListRef.current;
    if (list) itemListScrollTopRef.current = list.scrollTop;
  }, []);

  const restoreItemListScroll = useCallback(() => {
    const list = itemListRef.current;
    if (!list) return;
    list.scrollTop = itemListScrollTopRef.current;
  }, []);

  const dismissSearchKeyboard = useCallback(() => {
    const input = searchInputRef.current;
    if (!input || document.activeElement !== input) return;
    persistItemListScroll();
    input.blur();
  }, [persistItemListScroll]);

  const onDialogPointerDownCapture: React.PointerEventHandler<
    HTMLDivElement
  > = (event) => {
    const input = searchInputRef.current;
    if (!input || document.activeElement !== input) return;

    const target = event.target as Node;
    // Keep the keyboard only while interacting with the search field itself.
    if (target === input || input.contains(target)) return;

    persistItemListScroll();
    input.blur();
  };

  // Body scroll lock lives in useModalFocus. A second lock here (esp. with
  // documentElement.overflow + position:fixed) fought that unlock path and
  // left the funnel unscrollable on mobile after the keyboard dismissed.
  useEffect(() => {
    if (!isOpen) return;

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCloseRef.current();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen]);

  const displayItems = Array.from(selectedItems.values());
  const displayItemCount = displayItems.length;

  useLayoutEffect(() => {
    if (!isOpen) return;

    const open = keyboard.inset > 0;
    const wasOpen = keyboardOpenRef.current;
    keyboardOpenRef.current = open;

    if (wasOpen && !open) {
      restoreItemListScroll();
    }
  }, [isOpen, keyboard.inset, restoreItemListScroll]);

  useLayoutEffect(() => {
    if (!isOpen) return;
    restoreItemListScroll();
  }, [isOpen, displayItemCount, restoreItemListScroll]);

  if (!isOpen || typeof document === 'undefined') return null;
  const canEstimate = displayItems.some((item) => item.quantity > 0);

  const onOverlayClick: React.MouseEventHandler<HTMLDivElement> = (event) => {
    if (event.currentTarget === event.target) onClose();
  };

  const onChangeCount = (item: SelectedItem, newQuantity: number | null) => {
    const next = new Map(selectedItems);
    if (newQuantity === null) next.delete(item.category.id);
    else
      next.set(item.category.id, {
        quantity: newQuantity,
        category: item.category,
      });
    onSelectedItemsChange(next);
  };

  const onSubmit = () => {
    if (!canEstimate) return;
    const cuft = Array.from(selectedItems.values()).reduce(
      (acc, cur) => acc + cur.category.cuft * cur.quantity,
      0,
    );
    onClose(suggestedPlanForCUFT(cuft));
  };

  const pinToVisualViewport = keyboard.height > 0;

  return createPortal(
    <Overlay
      onClick={onOverlayClick}
      role="presentation"
      $vvHeight={keyboard.height || undefined}
      style={
        pinToVisualViewport
          ? {
              top: keyboard.offsetTop,
              left: keyboard.offsetLeft,
              width: keyboard.width,
              height: keyboard.height,
              right: 'auto',
              bottom: 'auto',
            }
          : undefined
      }
    >
      <Dialog
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="challenger-storage-calculator-title"
        onClick={(event) => event.stopPropagation()}
        onPointerDownCapture={onDialogPointerDownCapture}
      >
        <Header>
          <HeaderRow>
            <Title id="challenger-storage-calculator-title">
              What items are you storing?
            </Title>
            <CloseButton
              type="button"
              aria-label="Close"
              onClick={() => onClose()}
            >
              <img src={closeIcon} alt="" width={14} height={14} />
            </CloseButton>
          </HeaderRow>
          <Subtitle>
            The more items you add, the better we can estimate your storage
            plan.
          </Subtitle>
        </Header>

        <Body>
          <SearchSection>
            <StorageCalculatorSearch
              searchInputRef={searchInputRef}
              selectedItems={selectedItems}
              onSelectedItemsChange={onSelectedItemsChange}
              query={query}
              onQueryChange={setQuery}
              onDismissKeyboard={dismissSearchKeyboard}
            />
          </SearchSection>

          <ContentSlot>
            {displayItems.length > 0 && (
              <ItemList
                ref={itemListRef}
                onTouchStart={dismissSearchKeyboard}
                onScroll={() => {
                  persistItemListScroll();
                  dismissSearchKeyboard();
                }}
              >
                {displayItems.map((item) => (
                  <ItemRow key={item.category.id}>
                    <ItemName>{item.category.name}</ItemName>
                    <ItemActions>
                      {item.quantity === 0 && (
                        <RemoveButton
                          type="button"
                          onClick={() => onChangeCount(item, null)}
                        >
                          Remove
                        </RemoveButton>
                      )}
                      <Counter>
                        <CounterButton
                          type="button"
                          aria-label={`Decrease ${item.category.name}`}
                          onClick={() =>
                            onChangeCount(
                              item,
                              item.quantity > 0 ? item.quantity - 1 : null,
                            )
                          }
                        >
                          <img src={minusIcon} alt="" width={15} height={15} />
                        </CounterButton>
                        <CounterValue>{item.quantity}</CounterValue>
                        <CounterButton
                          type="button"
                          aria-label={`Increase ${item.category.name}`}
                          onClick={() => onChangeCount(item, item.quantity + 1)}
                        >
                          <img src={plusIcon} alt="" width={15} height={15} />
                        </CounterButton>
                      </Counter>
                    </ItemActions>
                  </ItemRow>
                ))}
              </ItemList>
            )}
          </ContentSlot>
        </Body>

        <Footer>
          <EstimateButton
            type="button"
            disabled={!canEstimate}
            onClick={onSubmit}
          >
            Estimate my plan
          </EstimateButton>
        </Footer>
      </Dialog>
    </Overlay>,
    document.body,
  );
};
